// Shared NASA SUPIN field state (Air → NASA SUPIN).
//
// Converting the feature tree to the windowed pattern means AirContent now
// mounts only while its "Inlet Parameters" window is open, not for the whole
// time the SUPIN sub-tab is active. Unlike the Rocket Nozzle calculator, these
// values ARE read at Generate time (usePromptSidebarActions.ts reads
// `supinParams` from context) — so they must survive the window being closed
// and reopened, not just persist for UX polish. The fields live in a tiny
// module-level store, subscribed via useSyncExternalStore — the same shape as
// tubeframeFieldsStore.ts — and SupinParamsSync (mounted always, alongside the
// window) publishes readiness from this store into homepage-context
// regardless of whether the window is open.

import { useCallback, useSyncExternalStore } from 'react';

export interface SupinFields {
  mach: string;
  altitude: string;
  qdyn: string;
  engineFlow: string;
  rampStages: string;
  rampAngle: string;
  coneAngle: string;
  numStages: string;
  parentMach: string;
  kstex: string;
  cfdGrids: boolean;
}

const INITIAL: SupinFields = {
  mach: '1.7',
  // Altitude is for Kfs=1 (the typical subsonic-to-low-supersonic case). K5
  // streamline-traced inlets are usually hypersonic — defined by qdyn instead;
  // AirContent auto-toggles which one it shows based on the active inlet type.
  altitude: '55000',
  qdyn: '1500',
  engineFlow: '383',
  rampStages: '3',
  rampAngle: '5.0',
  coneAngle: '12.0',
  numStages: '3',
  parentMach: '2.3',
  // K5 parent flow family. Default 0 = canonical Busemann ICFA (matches the
  // bundled SUPIN.K5.M*.Kstex0.in reference cases); 1 = internal-conical
  // parent flow for higher-Mach K5 cases (SUPIN.K5.M40.Kstex1.in).
  kstex: '0',
  cfdGrids: false,
};

let state: SupinFields = { ...INITIAL };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function setField<K extends keyof SupinFields>(key: K, value: SupinFields[K]) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  emit();
}

/** Reset to defaults — called when the SUPIN surface unmounts, so a
 *  module-level store doesn't retain stale values across navigation /
 *  conversations (matches the old AirContent useState that reset on
 *  unmount). */
export function resetSupinFields() {
  state = { ...INITIAL };
  emit();
}

export interface SupinFieldsApi extends SupinFields {
  setField: <K extends keyof SupinFields>(
    key: K,
    value: SupinFields[K],
  ) => void;
}

export function useSupinFields(): SupinFieldsApi {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...snapshot,
    setField: useCallback(setField, []),
  };
}
